/**
 * The loader's session, over `@zion/sdk-react/auth-core` (layered shell surfaces, D18, D19, WS-6
 * §3.4, Q4).
 *
 * The loader owns the session before any React code runs: it hydrates through the refresh cookie,
 * drives the silent refresh, the visibility refresh and the cross-tab sync, adopts the session a
 * sign-in page minted, and signs out. WS-8's services read it through the `ServiceSession` port
 * {@link createServiceSession} builds. The token store is auth-core's one module instance, which
 * the interim root's `AuthProvider` shares.
 */
import { sessionResponseSchema, type SessionResponse } from '@zion/sdk-contracts';
import {
  adoptSession,
  clearTokens,
  computeExpiresAt,
  extractClaims,
  getAccessToken,
  hydrateTokens,
  isTokenExpired,
  refreshAccessToken,
  revokeServerSession,
  setRefreshCallbacks,
  startRefreshRetry,
  startSessionSync,
  startSilentRefresh,
  startVisibilityRefresh,
  stopSessionSync,
  stopSilentRefresh,
  stopVisibilityRefresh,
  withRefreshLock,
} from '@zion/sdk-react/auth-core';
import { clearAppTokens, syncSessionToken } from '../auth/app-token-store.js';
import type { ServiceSession, ServiceSessionSnapshot } from '@zion/surface-host';

/** How long a boot whose refresh failed transiently holds before it settles signed out (R-38's bound). */
export const LOADER_BOOT_HOLD_MS = 15_000;

/** Where the loader's session is: still hydrating, signed in with a token, or signed out. */
export type SessionState =
  | { readonly status: 'pending' }
  | { readonly status: 'signed-in'; readonly accessToken: string }
  | { readonly status: 'signed-out' };

/**
 * The loader's session object. One per document.
 *
 * - `hydrate()` runs auth-core `hydrateTokens()` once per object; later calls return the same
 *   promise. A token resolves `signed-in` and starts the drivers; `null` resolves `signed-out`
 *   with no driver. A rejection (auth-core's `TransientRefreshError`) arms auth-core's one retry
 *   and the cross-tab sync,
 *   and holds until the first refresh callback (`signed-in`), a peer tab's token (`signed-in`), a
 *   session-expired callback (`signed-out`) or {@link LOADER_BOOT_HOLD_MS} (`signed-out`, retry
 *   cancelled). Never rejects.
 * - `current()` is `pending` until `hydrate` settles. After `yieldDrivers` it reads the shared
 *   token store, so the interim root's sign-out shows here.
 * - `adopt(session)` parses with `sessionResponseSchema`. A refusal, or a body auth-core cannot
 *   store, throws {@link SessionPayloadError} and changes nothing. A pass calls auth-core
 *   `adoptSession`, starts the drivers, notifies and returns `signed-in`.
 * - `signOut()` takes the held token as the bearer while its own `exp` lies in the future. When no
 *   token is held, or the held token's `exp` has passed or is absent, it refreshes once through
 *   the cookie and takes the fresh token, as `AuthProvider`'s `mintLogoutBearer` does (R-41,
 *   R-49): core-api's guard refuses an expired bearer with 401 before the logout runs, so the
 *   cookie family would survive. A refused or failed refresh sends no logout. It runs auth-core
 *   `revokeServerSession` under `withRefreshLock`, swallows every rejection, then calls `end`.
 * - `end()` clears both token stores, stops the three drivers and notifies `signed-out` once. A
 *   second call while signed out with no driver running does nothing.
 * - `subscribe(listener)` hears every state change, a peer tab's included; returns an idempotent
 *   unsubscribe. A listener that throws is reported to `console.error`; the rest run.
 * - `yieldDrivers()` stops the refresh, visibility and sync drivers and keeps the store, so the
 *   interim root's `AuthProvider` drives them for its lifetime. The next `adopt` or
 *   `reclaimDrivers` takes them back.
 * - `reclaimDrivers()` takes the drivers back after the interim root unmounts (review finding
 *   architecture-4). Boot calls it once a swap of `#root` away from the interim root has settled:
 *   the interim root's `AuthProvider` stops the three drivers in its unmount cleanup, which the
 *   React adapter runs one microtask after the unmount call (R-39, R-50), before the incoming
 *   mount's `ready` settles. When the shared store holds a token, it notifies `signed-in` with that
 *   token and starts the three drivers. When the store is empty (the interim root signed out), it
 *   runs `end`. A session that has not yielded, or has already taken its drivers back, does nothing.
 */
export interface LoaderSession {
  hydrate(): Promise<SessionState>;
  current(): SessionState;
  adopt(session: unknown): SessionState;
  signOut(): Promise<void>;
  end(): void;
  subscribe(listener: (state: SessionState) => void): () => void;
  yieldDrivers(): void;
  reclaimDrivers(): void;
}

/** Thrown by `adopt` for a body that is not a session; the store is unchanged. */
export class SessionPayloadError extends Error {
  override readonly name = 'SessionPayloadError';

  constructor() {
    super('the session payload is not a session response');
  }
}

const PENDING: SessionState = Object.freeze({ status: 'pending' });
const SIGNED_OUT: SessionState = Object.freeze({ status: 'signed-out' });

function signedIn(accessToken: string): SessionState {
  return Object.freeze({ status: 'signed-in', accessToken });
}

/**
 * The held access token while its own `exp` lies in the future, else `null`. auth-core's store
 * holds only a token `computeExpiresAt` decodes (`sessionStore.set` throws for any other), so the
 * read never throws; a token with no numeric `exp` decodes to `NaN` and reads as expired.
 */
function liveHeldToken(): string | null {
  const held = getAccessToken();
  if (held === null) return null;
  const expiresAt = computeExpiresAt(held);
  return Number.isFinite(expiresAt) && !isTokenExpired(expiresAt) ? held : null;
}

/**
 * The bearer a sign-out posts: the live held token, else one fresh refresh through the cookie,
 * else `null` when the refresh is refused or fails. It runs outside `withRefreshLock`, because
 * auth-core's refresh takes the same lock and a mint inside it deadlocks. Never rejects.
 */
function logoutBearer(): Promise<string | null> {
  return Promise.resolve(liveHeldToken() ?? refreshAccessToken().catch(() => null));
}

/** Create the document's session. No side effect until `hydrate`, `adopt`, `signOut` or `end`. */
export function createLoaderSession(): LoaderSession {
  let state: SessionState = PENDING;
  let hydration: Promise<SessionState> | null = null;
  let driving = false;
  let yielded = false;
  let endHold: ((settled: SessionState) => void) | null = null;
  const listeners = new Set<(state: SessionState) => void>();

  const set = (next: SessionState): void => {
    state = next;
    for (const listener of Array.from(listeners)) {
      try {
        listener(next);
      } catch (err) {
        console.error('[shell] a session listener threw', err);
      }
    }
  };

  const takeToken = (token: string): void => {
    syncSessionToken(token);
    set(signedIn(token));
    endHold?.(state);
  };

  const onPeer = (token: string | null): void => {
    if (token !== null) {
      takeToken(token);
      return;
    }
    // A peer signed out: auth-core already cleared the store and stopped the refresh drivers, and
    // posts nothing back. The sync stays subscribed, so a peer's next sign-in reaches this tab.
    clearAppTokens();
    syncSessionToken(null);
    set(SIGNED_OUT);
  };

  const end = (): void => {
    if (state.status === 'signed-out' && !driving && getAccessToken() === null) return;
    driving = false;
    clearTokens();
    clearAppTokens();
    syncSessionToken(null);
    stopSilentRefresh();
    stopVisibilityRefresh();
    stopSessionSync();
    set(SIGNED_OUT);
    endHold?.(state);
  };

  const startDrivers = (): void => {
    driving = true;
    yielded = false;
    setRefreshCallbacks(takeToken, () => end());
    startSilentRefresh();
    startVisibilityRefresh();
    startSessionSync(onPeer);
  };

  const holdForRetry = (): Promise<SessionState> =>
    new Promise<SessionState>((resolve) => {
      const timer = setTimeout(() => {
        endHold = null;
        stopSilentRefresh();
        stopSessionSync();
        set(SIGNED_OUT);
        resolve(state);
      }, LOADER_BOOT_HOLD_MS);
      endHold = (settled) => {
        endHold = null;
        clearTimeout(timer);
        if (settled.status === 'signed-in') startDrivers();
        resolve(settled);
      };
      setRefreshCallbacks(takeToken, () => end());
      startSessionSync(onPeer);
      startRefreshRetry();
    });

  const runHydrate = async (): Promise<SessionState> => {
    let token: string | null;
    try {
      token = await hydrateTokens();
    } catch {
      // auth-core rejects with `TransientRefreshError` alone: a refusal resolves `null` instead.
      return holdForRetry();
    }
    if (token === null) {
      set(SIGNED_OUT);
      return state;
    }
    syncSessionToken(token);
    set(signedIn(token));
    startDrivers();
    return state;
  };

  return {
    hydrate() {
      hydration ??= runHydrate();
      return hydration;
    },
    current() {
      if (!yielded) return state;
      const token = getAccessToken();
      return token === null ? SIGNED_OUT : signedIn(token);
    },
    adopt(session) {
      const parsed = sessionResponseSchema.safeParse(session);
      if (!parsed.success) throw new SessionPayloadError();
      const body: SessionResponse = parsed.data;
      try {
        adoptSession(body);
      } catch {
        throw new SessionPayloadError();
      }
      syncSessionToken(body.accessToken);
      set(signedIn(body.accessToken));
      startDrivers();
      return state;
    },
    async signOut() {
      const token = await logoutBearer();
      if (token !== null) {
        const bearer = token;
        await withRefreshLock(() => revokeServerSession(bearer)).catch(() => undefined);
      }
      end();
    },
    end,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    yieldDrivers() {
      if (!driving) return;
      driving = false;
      yielded = true;
      stopSilentRefresh();
      stopVisibilityRefresh();
      stopSessionSync();
    },
    reclaimDrivers() {
      if (!yielded) return;
      yielded = false;
      const token = getAccessToken();
      if (token === null) {
        end();
        return;
      }
      syncSessionToken(token);
      set(signedIn(token));
      startDrivers();
    },
  };
}

const HYDRATING: ServiceSessionSnapshot = Object.freeze({
  status: 'hydrating',
  token: null,
  userId: null,
  orgId: null,
  orgRoles: Object.freeze([]),
  orgPermissions: Object.freeze([]),
});
const ANONYMOUS: ServiceSessionSnapshot = Object.freeze({ ...HYDRATING, status: 'anonymous' });

/**
 * The `authenticated` snapshot WS-8's session port reports for one access token.
 *
 * Callers: `createServiceSession` for a `signed-in` state, and the interim root's
 * `SavedStateOwnerBinder`, which binds the saved-state owner to the token `AuthProvider` adopted
 * (R-78). One derivation serves both, so the two cannot name a different owner for one token.
 *
 * @param accessToken Any string. The function verifies no signature: the claims it reads pick
 *   whose saved state a tab keeps, and they authorize nothing.
 * @returns A frozen snapshot: `status: 'authenticated'`, `token` as given, and `userId`, `orgId`,
 *   `orgRoles` and `orgPermissions` from auth-core `extractClaims(accessToken)`. A token whose
 *   payload does not decode gives `userId` and `orgId` `null` and both lists empty, which is what
 *   `extractClaims` answers. It never throws and has no side effect.
 */
export function serviceSnapshotForToken(accessToken: string): ServiceSessionSnapshot {
  const claims = extractClaims(accessToken);
  return Object.freeze({
    status: 'authenticated',
    token: accessToken,
    userId: claims.sub,
    orgId: claims.orgId,
    orgRoles: Object.freeze([...claims.orgRoles]),
    orgPermissions: Object.freeze([...claims.orgPermissions]),
  });
}

/**
 * WS-8's session port over `session`.
 *
 * `current()` maps `pending` to `hydrating` and `signed-out` to `anonymous`, both with every other
 * field `null` or `[]`. `signed-in` maps to `authenticated` with `token`, and `userId`, `orgId`,
 * `orgRoles` and `orgPermissions` from auth-core `extractClaims(token)`. The snapshot is frozen and
 * keeps its identity until the session changes. `subscribe` hears every `LoaderSession` change and
 * returns an idempotent unsubscribe.
 */
export function createServiceSession(session: LoaderSession): ServiceSession {
  let lastState: SessionState | null = null;
  let lastSnapshot: ServiceSessionSnapshot = HYDRATING;
  const snapshotOf = (state: SessionState): ServiceSessionSnapshot => {
    if (state.status === 'pending') return HYDRATING;
    if (state.status === 'signed-out') return ANONYMOUS;
    return serviceSnapshotForToken(state.accessToken);
  };
  return {
    current() {
      const state = session.current();
      const sameAsLast =
        lastState !== null &&
        lastState.status === state.status &&
        (state.status !== 'signed-in' || (lastState.status === 'signed-in' && lastState.accessToken === state.accessToken));
      if (!sameAsLast) {
        lastState = state;
        lastSnapshot = snapshotOf(state);
      }
      return lastSnapshot;
    },
    subscribe(listener) {
      return session.subscribe(() => listener());
    },
  };
}
